import { Navigate, RouterProvider, createBrowserRouter, useLocation } from 'react-router-dom';

import { ErrorBoundary } from '../components/common/ErrorBoundary';
import { ProtectedRoute } from '../components/common/ProtectedRoute';
import { AssistantProvider } from '../features/analytics/assistant/AssistantContext';
import HomePage from '../features/analytics/home/HomePage';
import { AppShell } from '../features/analytics/shell/AppShell';

/**
 * Routing.
 *
 * Every surface has an address. The app previously lived at a single `/` and
 * switched screens with a `view` state variable, so nothing could be linked,
 * bookmarked, reloaded or reached with the back button — including the chart
 * builder, which is where users spend most of their time.
 *
 * `/login` hands off to Identity Studio, the suite's IdP, and `/auth/callback` is
 * where it returns. Whether the gate below actually gates is the DEPLOYMENT's
 * answer, not this file's: with no IdP configured the app runs open and the gate
 * waves everyone through. `/share/:token` stays outside either way, and is public
 * in a stronger sense — it needs no credential at all, and the backend serves it
 * from a frozen snapshot.
 *
 * Two external contracts are preserved exactly, because other products link into
 * this one:
 *
 * - `/?dashboard=<id>` — the Vaarta chatbot's "Open in Analytics" button.
 *   Redirected to `/dashboards/<id>`, carrying any other query params along.
 * - `?vaarta_sso=<token>` — the token hand-off, consumed in `main.tsx` before the
 *   app mounts (see the note there). It no longer admits anyone; it supplies the
 *   bearer forwarded to the platform on data reads.
 */

/** What the first paint shows while the router resolves a lazily-loaded screen. */
function RouteFallback() {
  return (
    <div className="grid min-h-dvh place-items-center bg-bg">
      <p className="text-xs text-fg-muted" role="status">
        Loading…
      </p>
    </div>
  );
}

/** Honour the chatbot's legacy deep link into a specific board. */
function HomeOrLegacyDashboard() {
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const dashboardId = params.get('dashboard');
  if (dashboardId) {
    params.delete('dashboard');
    const rest = params.toString();
    return <Navigate to={`/dashboards/${dashboardId}${rest ? `?${rest}` : ''}`} replace />;
  }
  return <HomePage />;
}

// Lazily-loaded surfaces. React Router resolves these before rendering, and the
// shell shows a progress bar meanwhile — better than a layout that flashes empty.
function lazyRoute(load: () => Promise<{ default: React.ComponentType }>) {
  return async () => ({ Component: (await load()).default });
}

export const router = createBrowserRouter([
  {
    path: '/login',
    lazy: lazyRoute(() => import('../pages/LoginPage')),
  },
  {
    // ⭐ AG-1803 — the way IN, on this application's own origin. `POST /v1/auth/signup` has
    // answered here since 2026-09-15 and there was no page and no link, so the only way to
    // get an account was for somebody to create one for you.
    path: '/signup',
    lazy: lazyRoute(() => import('../pages/SignupPage')),
  },
  {
    // ⭐ AG-1803 — the tenant boundary, on our own origin. ⛔ NOT the signup plan's own
    // `redirect`, which is `/app/<key>/onboarding` on the HOSTED Identity Studio host.
    path: '/onboarding',
    lazy: lazyRoute(() => import('../pages/OnboardingPage')),
  },
  {
    // AG-1804 — recovery on this application's own origin. Analytics mentioned a forgotten
    // password only in PROSE under "Continue with Vaarta"; there was no link. Both the
    // button and that prose are gone (AG-1989); this route is the whole of recovery now.
    path: '/forgot-password',
    lazy: lazyRoute(() => import('../pages/ForgotPasswordPage')),
  },
  {
    // The landing for the emailed token. ⚠️ Not yet where Identity Studio's email points —
    // that link is built on its own origin — see identity-studio docs/suite/auth-contract §5.
    path: '/reset-password',
    lazy: lazyRoute(() => import('../pages/ResetPasswordPage')),
  },
  {
    // AG-1803 — an unverified account could neither sign in (403 "Email not verified") nor
    // fix that from anywhere in this application. ⚠️ Not yet where Identity Studio's
    // verification email points — that needs `login_config.verify_email_url`, which is
    // opt-in per application and unset.
    path: '/verify-email',
    lazy: lazyRoute(() => import('../pages/VerifyEmailPage')),
  },
  {
    // AG-1805 — the ONLY landing page for an emailed sign-in link. Measured on the hosted
    // host 2026-09-14: `/app/{key}/login`, `/reset` and `/verify` answer 200 and
    // `/app/{key}/magic-link` answers 404, the same as a nonsense path. There is no hosted
    // page for this journey to fall back to, which is why Identity Studio refuses an
    // unregistered `callback_url` outright rather than mailing a dead link (AG-1942).
    path: '/auth/magic-link',
    lazy: lazyRoute(() => import('../pages/MagicLinkPage')),
  },
  {
    // Where Identity Studio returns the browser, carrying a one-time code.
    path: '/auth/callback',
    lazy: lazyRoute(() => import('../pages/AuthCallbackPage')),
  },
  {
    // Public share view — rendered from a snapshot, no credential involved.
    path: '/share/:token',
    lazy: lazyRoute(() => import('../pages/PublicSharePage')),
  },
  {
    path: '/',
    element: (
      <ProtectedRoute>
        <AssistantProvider>
          <AppShell />
        </AssistantProvider>
      </ProtectedRoute>
    ),
    errorElement: (
      <ErrorBoundary subject="The app">
        <div />
      </ErrorBoundary>
    ),
    // The router resolves a lazy route before rendering it; this is what the
    // very first paint shows while that happens. Without it React Router warns,
    // and the screen is briefly blank rather than obviously loading.
    HydrateFallback: RouteFallback,
    children: [
      { index: true, element: <HomeOrLegacyDashboard /> },
      { path: 'charts', lazy: lazyRoute(() => import('../features/analytics/library/ChartsPage')) },
      { path: 'charts/new', lazy: lazyRoute(() => import('../features/analytics/builder/BuilderPage')) },
      {
        path: 'charts/:chartId',
        lazy: lazyRoute(() => import('../features/analytics/library/ChartDetailPage')),
      },
      {
        path: 'charts/:chartId/edit',
        lazy: lazyRoute(() => import('../features/analytics/builder/BuilderPage')),
      },
      {
        path: 'dashboards',
        lazy: lazyRoute(() => import('../features/analytics/dashboard/DashboardsPage')),
      },
      {
        path: 'dashboards/:dashboardId',
        lazy: lazyRoute(() => import('../features/analytics/dashboard/DashboardDetailPage')),
      },
      { path: 'data', lazy: lazyRoute(() => import('../features/analytics/sources/DataSourcesPage')) },
      {
        path: 'metrics',
        lazy: lazyRoute(() => import('../features/analytics/metrics/MetricsPage')),
      },
      { path: 'settings', lazy: lazyRoute(() => import('../features/analytics/settings/SettingsPage')) },
      { path: '*', lazy: lazyRoute(() => import('../pages/NotFoundPage')) },
    ],
  },
]);

export default function App() {
  return <RouterProvider router={router} />;
}
